import React, { useState, useEffect } from 'react';
import { getOrders, getOrderStatistics, createOrder, updateOrder, updateOrderStatus, deleteOrder, getCustomers, getServices, getProducts, requestRefund, getRefundStatistics, getStores } from '../api';
import Pagination from '../components/Pagination';

function Orders() {
  const user = JSON.parse(sessionStorage.getItem('health_user') || '{}');
  const isAdmin = user?.role === 'admin';
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [refundStats, setRefundStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('全部');
  const [storeFilter, setStoreFilter] = useState('');
  const [stores, setStores] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [refundItem, setRefundItem] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [servicesList, setServicesList] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0, totalPages: 0 });
  const [sortInfo, setSortInfo] = useState({ field: 'created_at', order: 'desc' });
  const [formData, setFormData] = useState({ customer_id: '', type: '服务', service_id: '', service_name: '', product_id: '', product_name: '', total_times: 1, order_date: '', notes: '' });
  const [refundForm, setRefundForm] = useState({ refund_reason: '', refund_amount: '' });

  const statuses = ['全部', '待处理', '进行中', '已完成', '已取消'];

  useEffect(() => { fetchStores(); }, []);
  useEffect(() => { fetchOrders(); fetchOptions(); }, [filterStatus, storeFilter, pagination.page, pagination.pageSize, sortInfo]);

  const fetchStores = async () => {
    if (!isAdmin) return;
    try {
      const res = await getStores();
      if (res.data.success) setStores(res.data.data);
    } catch (e) { console.error(e); }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const map = { '全部': '', '待处理': 'pending', '进行中': 'processing', '已完成': 'completed', '已取消': 'cancelled' };
      const [ordersRes, statsRes, refundRes] = await Promise.all([
        getOrders({
          status: map[filterStatus],
          store_id: storeFilter || undefined,
          page: pagination.page,
          pageSize: pagination.pageSize,
          sortBy: sortInfo.field,
          sortOrder: sortInfo.order
        }),
        getOrderStatistics(),
        getRefundStatistics()
      ]);
      if (ordersRes.data.success) {
        setOrders(ordersRes.data.data);
        if (ordersRes.data.pagination) {
          setPagination(prev => ({ ...prev, ...ordersRes.data.pagination }));
        }
      }
      if (statsRes.data.success) setStats(statsRes.data.data);
      if (refundRes.data.success) setRefundStats(refundRes.data.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchOptions = async () => {
    try {
      // 根据门店筛选客户
      const storeId = isAdmin ? user.store_id : user.store_id;
      const [cRes, sRes, pRes] = await Promise.all([
        getCustomers(storeId ? {store_id: storeId} : {}),
        getServices(storeId ? {store_id: storeId} : {}),
        getProducts(storeId ? {store_id: storeId} : {})
      ]);
      if (cRes.data.success) setCustomers(cRes.data.data);
      if (sRes.data.success) setServicesList(sRes.data.data);
      if (pRes.data.success) setProductsList(pRes.data.data);
    } catch (e) { console.error(e); }
  };

  const handleRequestRefund = async (order) => {
    setRefundItem(order);
    setRefundForm({ refund_reason: '', refund_amount: order.amount });
    setShowRefundModal(true);
  };

  const submitRefund = async (e) => {
    e.preventDefault();
    try {
      await requestRefund(refundItem.id, refundForm);
      setShowRefundModal(false);
      setRefundItem(null);
      fetchOrders();
    } catch (e) { console.error(e); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const statusMap = { '待处理': 'pending', '进行中': 'processing', '已完成': 'completed', '已取消': 'cancelled' };
      const data = { ...formData, status: statusMap[formData.status] || 'pending' };
      if (editItem) await updateOrder(editItem.id, data);
      else await createOrder(data);
      setShowModal(false);
      resetForm();
      fetchOrders();
    } catch (e) {
      alert(e.response?.data?.message || e.message);
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    const statusMap = { pending: '待处理', processing: '进行中', completed: '已完成', cancelled: '已取消' };
    // 判断是服务还是产品类型
    const orderType = item.service_id ? '服务' : '产品';
    setFormData({
      customer_id: item.customer_id,
      type: orderType,
      service_id: item.service_id || '',
      service_name: item.service_name || '',
      product_id: item.product_id || '',
      product_name: item.product_name || '',
      total_times: item.total_times || 1,
      amount: item.amount,
      status: statusMap[item.status] || '待处理',
      order_date: item.order_date,
      notes: item.notes || '',
      store_id: item.store_id || '',
      service_duration: item.service_duration || ''
    });
    setShowModal(true);
  };

  const handleDetail = (item) => {
    setDetailItem(item);
  };

  const handleDelete = async (id) => {
    if (window.confirm('确定删除?')) {
      try { await deleteOrder(id); fetchOrders(); }
      catch (e) { alert(e.response?.data?.message || '删除失败'); }
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    const map = { '待处理': 'pending', '进行中': 'processing', '已完成': 'completed', '已取消': 'cancelled' };
    try { await updateOrderStatus(id, map[newStatus]); fetchOrders(); }
    catch (e) { console.error(e); }
  };

  const resetForm = () => {
    setEditItem(null);
    setFormData({ customer_id: '', type: '服务', service_id: '', service_name: '', product_id: '', product_name: '', total_times: 1, order_date: new Date().toISOString().split('T')[0], notes: '' });
  };

  
  const getStatusBadge = (status) => {
    const map = { pending: '待处理', processing: '进行中', completed: '已完成', cancelled: '已取消', active: '进行中' };
    const cls = { pending: 'badge-yellow', processing: 'badge-gray', completed: 'badge-green', cancelled: 'badge-red', active: 'badge-gray' };
    return <span className={`status-badge ${cls[status]}`}>{map[status] || status}</span>;
  };

  const getRefundBadge = (status) => {
    if (!status || status === 'none') return null;
    const map = { pending: '待退款', approved: '已退款', rejected: '已拒绝' };
    const cls = { pending: 'badge-yellow', approved: 'badge-green', rejected: 'badge-red' };
    return <span className={`status-badge ${cls[status]}`}>{map[status] || status}</span>;
  };

  const exportCSV = () => {
    const csv = [['订单号', '客户', '门店', '服务项目', '金额', '状态', '日期'], ...orders.map(o => [o.order_no, o.customer_name, o.store_name || '', o.service_name, o.amount, o.status, o.order_date])].map(r => r.join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `订单_${new Date().toISOString().split('T')[0]}.csv`; a.click();
  };

  const handleServiceChange = (e) => {
    const svc = servicesList.find(s => s.id === parseInt(e.target.value));
    setFormData({ ...formData, service_id: e.target.value, service_name: svc ? svc.name : '', amount: svc ? svc.price : '' });
  };

  const handleProductChange = (e) => {
    const prod = productsList.find(p => p.id === parseInt(e.target.value));
    setFormData({ ...formData, product_id: e.target.value, product_name: prod ? prod.title : '', amount: prod ? prod.price : '' });
  };

  const closeDetail = () => { setDetailItem(null); };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handlePageSizeChange = (pageSize) => {
    setPagination(prev => ({ ...prev, pageSize, page: 1 }));
  };

  const handleSortChange = (field, order) => {
    setSortInfo({ field, order });
  };

  return (
    <>
      {/* 统计 */}
      <div className="stats-cards">
        <div className="stats-row">
          <div className="stat-box"><div className="stat-label">订单总数</div><div className="stat-value">{stats.totalOrders || 0}</div></div>
          <div className="stat-box"><div className="stat-label">总收入</div><div className="stat-value green">¥{(stats.totalAmount || 0).toLocaleString()}</div></div>
          <div className="stat-box"><div className="stat-label">进行中</div><div className="stat-value orange">{stats.processingCount || 0}</div></div>
          <div className="stat-box"><div className="stat-label">已完成</div><div className="stat-value green">{stats.completedCount || 0}</div></div>
        </div>
      </div>

      {/* 订单列表 */}
      <div className="content-card">
        <div className="section-title">订单管理</div>

        <div className="toolbar-row">
          <div className="toolbar-left">
            <select className="filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              {statuses.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {isAdmin && (
              <select className="filter-select" value={storeFilter} onChange={e => setStoreFilter(e.target.value)}>
                <option value="">全部门店</option>
                {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )}
          </div>
          <button className="action-btn btn-blue" onClick={() => { resetForm(); setShowModal(true); }}>+ 新建订单</button>
        </div>

        {loading ? <div className="loading-text">加载中...</div> : (
          <>
            <table className="basic-table">
              <thead>
                <tr><th className="no-cell">序号</th><th>门店</th><th>类型</th><th>客户</th><th>产品/服务</th><th>金额</th><th>状态</th><th>日期</th><th>操作</th></tr>
              </thead>
              <tbody>
                {orders.length > 0 ? orders.map((o, i) => (
                  <tr key={i}>
                    <td className="no-cell">{i + 1}</td>
                    <td>{o.store_name || '-'}</td>
                    <td>{o.type === '服务' ? '服务' : '产品'}</td>
                    <td>{o.customer_name}</td>
                    <td>{o.product_name}</td>
                    <td>¥{o.amount}</td>
                    <td>{o.status === 'active' ? '进行中' : o.status === 'completed' ? '已完成' : '未知'}</td>
                    <td>{o.order_date ? o.order_date.split(' ')[0] : '-'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="btn-xs btn-gray" onClick={() => handleDetail(o)}>详情</button>
                        <button className="btn-xs btn-blue" onClick={() => handleEdit(o)}>编辑</button>
                        {o.status === 'completed' && (!o.refund_status || o.refund_status === 'none') &&
                          <button className="btn-xs btn-orange" onClick={() => handleRequestRefund(o)}>退款</button>
                        }
                        <button className="btn-xs btn-red" onClick={() => handleDelete(o.id)}>删除</button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan="10" style={{ textAlign: 'center', color: '#999' }}>暂无数据</td></tr>
                )}
              </tbody>
            </table>

            <Pagination
              pagination={pagination}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </>
        )}

        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <button className="action-btn btn-green" onClick={exportCSV}>导出</button>
        </div>
      </div>

      {/* 新建/编辑弹窗 */}
      {showModal && (
        <div className="modal-mask">
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-title"><span>{editItem ? '编辑订单' : '新建订单'}</span><span className="modal-close" onClick={() => setShowModal(false)}>×</span></div>
            <form onSubmit={handleSubmit}>
              <div className="modal-content">
                <div className="form-tip">带 <span style={{color:'#ff4d4f'}}>*</span> 号的为必填项</div>
                {isAdmin && (
                  <div className="form-row"><div className="form-field"><label className="form-label">门店</label>
                    <select className="form-input" value={formData.store_id} onChange={async e => {
                      const newStoreId = e.target.value;
                      setFormData({...formData, store_id: newStoreId, customer_id: ''});
                      // 根据门店筛选客户
                      const cRes = await getCustomers(newStoreId ? {store_id: newStoreId} : {});
                      if (cRes.data.success) setCustomers(cRes.data.data || []);
                    }} disabled={!!editItem}>
                      <option value="">请选择</option>
                      {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div></div>
                )}
                <div className="form-row"><div className="form-field"><label className="form-label">客户 *</label><select className="form-input" value={formData.customer_id} onChange={e => setFormData({...formData, customer_id: e.target.value})} required disabled={!!editItem}><option value="">请选择</option>{customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div></div>
                <div className="form-row"><div className="form-field"><label className="form-label">类型</label><select className="form-input" value={formData.type} onChange={e => setFormData({...formData, type: e.target.value, service_id: '', product_id: ''})} disabled={!!editItem}><option value="服务">服务</option><option value="产品">产品</option></select></div></div>
                {formData.type === '服务' ? (
                  <div className="form-row"><div className="form-field"><label className="form-label">服务项目 *</label><select className="form-input" value={formData.service_id} onChange={handleServiceChange} required disabled={!!editItem}><option value="">请选择</option>{servicesList.map(s => <option key={s.id} value={s.id}>{s.name} - ¥{s.price}</option>)}</select></div></div>
                ) : (
                  <div className="form-row"><div className="form-field"><label className="form-label">产品 *</label><select className="form-input" value={formData.product_id} onChange={handleProductChange} required disabled={!!editItem}><option value="">请选择</option>{productsList.map(p => <option key={p.id} value={p.id}>{p.title} - ¥{p.price}</option>)}</select></div></div>
                )}
                <div className="form-row"><div className="form-field"><label className="form-label">{formData.type === '服务' ? '次数' : '数量'}</label><input className="form-input" type="number" value={formData.total_times} onChange={e => setFormData({...formData, total_times: e.target.value})} /></div></div>
                <div className="form-row"><div className="form-field"><label className="form-label">日期</label><input className="form-input" type="date" value={formData.order_date} onChange={e => setFormData({...formData, order_date: e.target.value})} disabled={!!editItem} /></div></div>
                <div className="form-row"><div className="form-field"><label className="form-label">备注</label><textarea className="form-input" rows="3" value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} /></div></div>
              </div>
              <div className="modal-foot"><button type="button" style={{ marginRight: 12 }} onClick={() => setShowModal(false)}>取消</button><button type="submit" className="action-btn btn-blue">保存</button></div>
            </form>
          </div>
        </div>
      )}

      {/* 详情弹窗 */}
      {detailItem && (
        <div className="modal-mask">
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-title">
              <span>订单详情</span>
              <span className="modal-close" onClick={closeDetail}>×</span>
            </div>
            <div className="modal-content">
              <div className="detail-section">
                <div className="detail-section-title">基本信息</div>
                <div className="detail-row"><span className="detail-label">订单号：</span><span className="detail-value">{detailItem.order_no}</span></div>
                <div className="detail-row"><span className="detail-label">创建时间：</span><span className="detail-value">{detailItem.created_at ? detailItem.created_at.replace('T', ' ').substring(0, 16) : '-'}</span></div>
              </div>
              <div className="detail-section">
                <div className="detail-section-title">客户信息</div>
                <div className="detail-row"><span className="detail-label">客户：</span><span className="detail-value">{detailItem.customer_name}</span></div>
                <div className="detail-row"><span className="detail-label">电话：</span><span className="detail-value">{detailItem.customer_phone ? detailItem.customer_phone.replace(/(\d{3})\d+(\d{4})/, '$1****$2') : '-'}</span></div>
                <div className="detail-row"><span className="detail-label">地址：</span><span className="detail-value">{detailItem.customer_address || '-'}</span></div>
              </div>
              <div className="detail-section">
                <div className="detail-section-title">{detailItem.type === '服务' ? '服务信息' : '产品信息'}</div>
                {detailItem.type === '服务' ? (
                  <>
                    <div className="detail-row"><span className="detail-label">服务项目：</span><span className="detail-value">{detailItem.service_name}</span></div>
                    <div className="detail-row"><span className="detail-label">服务类型：</span><span className="detail-value">{detailItem.service_type || '-'}</span></div>
                    <div className="detail-row"><span className="detail-label">服务价格：</span><span className="detail-value">¥{detailItem.service_price || detailItem.amount}</span></div>
                  </>
                ) : (
                  <>
                    <div className="detail-row"><span className="detail-label">产品名称：</span><span className="detail-value">{detailItem.product_name}</span></div>
                    <div className="detail-row"><span className="detail-label">产品单价：</span><span className="detail-value">¥{detailItem.product_price || detailItem.amount}</span></div>
                  </>
                )}
              </div>
              <div className="detail-section">
                <div className="detail-section-title">订单信息</div>
                <div className="detail-row"><span className="detail-label">订单金额：</span><span className="detail-value red">¥{detailItem.amount || detailItem.total_amount || 0}</span></div>
                <div className="detail-row"><span className="detail-label">订单状态：</span><span className="detail-value">{getStatusBadge(detailItem.status)}</span></div>
                <div className="detail-row"><span className="detail-label">开始时间：</span><span className="detail-value">{detailItem.order_date || detailItem.purchase_date || '-'}</span></div>
                <div className="detail-row"><span className="detail-label">完成时间：</span><span className="detail-value">{detailItem.completed_date || '-'}</span></div>
              </div>
              <div className="detail-section">
                <div className="detail-section-title">备注</div>
                <div className="detail-notes">{detailItem.notes || '无'}</div>
              </div>
            </div>
            <div className="modal-foot">
              <button className="action-btn btn-blue" onClick={closeDetail}>关闭</button>
            </div>
          </div>
        </div>
      )}

      {showRefundModal && (
        <div className="modal-mask">
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-title">
              <span>申请退款</span>
              <span className="modal-close" onClick={() => setShowRefundModal(false)}>×</span>
            </div>
            <form onSubmit={submitRefund}>
              <div className="modal-content">
                <div className="form-row">
                  <div className="form-field">
                    <label className="form-label">订单号</label>
                    <div style={{ padding: '8px 0', color: '#666' }}>{refundItem?.order_no}</div>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-field">
                    <label className="form-label">退款金额</label>
                    <input className="form-input" type="number" value={refundForm.refund_amount}
                      onChange={e => setRefundForm({ ...refundForm, refund_amount: e.target.value })} required />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-field">
                    <label className="form-label">退款原因</label>
                    <textarea className="form-input" rows={3} value={refundForm.refund_reason}
                      onChange={e => setRefundForm({ ...refundForm, refund_reason: e.target.value })}
                      placeholder="请输入退款原因" required />
                  </div>
                </div>
              </div>
              <div className="modal-foot">
                <button type="button" style={{ marginRight: 12 }} onClick={() => setShowRefundModal(false)}>取消</button>
                <button type="submit" className="action-btn btn-orange">提交退款</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default Orders;